import { useUnit } from 'effector-react';
import { BarChart3 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { positionSelected } from '../../entities/position';
import type { Schemas } from '../../shared/api/client';
import {
  formatDate,
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  formatRatio,
  formatShares,
  parseDate,
  toNumber,
} from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { routes } from '../../shared/routing';
import {
  ChartCard,
  DataTable,
  EmptyState,
  KpiTile,
  LineChart,
  PlValue,
  Spinner,
  Tooltip,
  sortRows,
  type Column,
} from '../../shared/ui';
import { AppShell } from '../../widgets/app-shell';
import { $sort, $stats, sortChanged, statsRequested } from './model';

type Row = Schemas['ClosedRowOut'];

export function StatsPage() {
  const { t } = useTranslation();
  const [stats, request] = useUnit([$stats, statsRequested]);
  useEffect(() => {
    request();
  }, [request]);
  return (
    <AppShell page="stats" crumbs={[t('nav.stats')]}>
      <div className="mx-auto w-full max-w-6xl space-y-4 px-6 py-6 max-sm:px-4">
        <h1 className="text-lg font-semibold">{t('nav.stats')}</h1>
        {stats === null ? (
          <div className="flex justify-center py-16">
            <Spinner label={t('common.loading')} />
          </div>
        ) : stats.rows.length === 0 ? (
          <EmptyState
            className="py-16"
            icon={<BarChart3 />}
            title={t('stats.emptyTitle')}
            body={t('stats.emptyBody')}
          />
        ) : (
          <>
            <Summary stats={stats} />
            <div className="grid gap-3 xl:grid-cols-2">
              <PointsChart
                title={t('stats.cumulativePl')}
                label={t('stats.cumulativePlLabel')}
                points={stats.cumulative_pl}
                step
              />
              <PointsChart
                title={t('stats.deployed')}
                label={t('stats.deployedLabel')}
                points={stats.deployed}
                step
                area
              />
            </div>
            <ClosedTable rows={stats.rows} />
          </>
        )}
      </div>
    </AppShell>
  );
}

function Summary({ stats }: { stats: Schemas['StatsOut'] }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const s = stats.summary;
  const pl = toNumber(s.realized_pl);
  const diff = s.xirr !== null && s.benchmark_xirr !== null ? s.xirr - s.benchmark_xirr : null;
  return (
    <div
      className="grid grid-cols-2 gap-3 xl:grid-cols-5"
      role="group"
      aria-label={t('stats.summary')}
    >
      <KpiTile
        label={t('stats.realized')}
        tone={pl < 0 ? 'negative' : undefined}
        value={formatMoney(s.realized_pl, locale, { signed: true })}
        detail={t('stats.maxDeployed', { amount: formatMoney(s.max_deployed, locale) })}
      />
      <KpiTile
        label={t('stats.xirr')}
        value={s.xirr === null ? '—' : formatRatio(s.xirr, locale, { signed: true })}
        detail={t('stats.xirrHint')}
      />
      <KpiTile
        label={t('stats.medianDays')}
        value={s.median_days === null ? '—' : formatNumber(s.median_days, locale, 1)}
        detail={t('stats.calendarDays')}
      />
      <KpiTile label={t('stats.closed')} value={formatNumber(s.closed_count, locale)} />
      <KpiTile
        label={t('stats.vsBuyHold')}
        value={diff === null ? '—' : formatRatio(diff, locale, { signed: true })}
        detail={
          s.benchmark_xirr === null
            ? t('stats.noBenchmark')
            : t('stats.benchmark', {
                rate: formatRatio(s.benchmark_xirr, locale, { signed: true }),
              })
        }
      />
    </div>
  );
}

function PointsChart({
  title,
  label,
  points,
  step = false,
  area = false,
}: {
  title: string;
  label: string;
  points: Schemas['PointOut'][];
  step?: boolean;
  area?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const series = points.map((p) => ({ x: parseDate(p.day), y: toNumber(p.value) }));
  return (
    <ChartCard
      title={title}
      labels={{ viewAsTable: t('charts.viewAsTable'), viewAsChart: t('charts.viewAsChart') }}
      table={
        <DataTable
          dense
          caption={title}
          columns={[
            {
              id: 'day',
              header: t('charts.date'),
              cell: (row) => formatDate(row.day, locale, 'UTC'),
            },
            {
              id: 'value',
              header: title,
              numeric: true,
              cell: (row) => formatMoney(row.value, locale),
            },
          ]}
          rows={points}
          rowKey={(row) => row.day}
        />
      }
    >
      {series.length > 1 ? (
        <LineChart
          label={label}
          series={[{ id: 'value', label: title, color: 1, points: series, step, area }]}
          height={200}
          formatY={(v) => formatMoney(v, locale, { places: 0 })}
          formatXTick={(d) => formatMonth(d, locale)}
          formatXLong={(d) => formatDate(d, locale, 'UTC')}
        />
      ) : (
        <p className="py-10 text-center text-sm text-text-3">{t('stats.notEnoughPoints')}</p>
      )}
    </ChartCard>
  );
}

function ClosedTable({ rows }: { rows: Row[] }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [sort, setSort, select] = useUnit([$sort, sortChanged, positionSelected]);
  const columns: Column<Row>[] = [
    {
      id: 'opened',
      header: t('stats.cols.opened'),
      cell: (r) => formatDate(r.opened, locale, 'UTC'),
      sortValue: (r) => r.opened,
    },
    {
      id: 'closed',
      header: t('stats.cols.closed'),
      cell: (r) => formatDate(r.closed, locale, 'UTC'),
      sortValue: (r) => r.closed,
    },
    {
      id: 'symbol',
      header: t('stats.cols.symbol'),
      cell: (r) => <span className="font-medium">{r.symbol}</span>,
      sortValue: (r) => r.symbol,
    },
    {
      id: 'deepest',
      header: t('stats.cols.deepest'),
      numeric: true,
      cell: (r) => (r.deepest_level === 0 ? t('ladder.entryShort') : `L${String(r.deepest_level)}`),
      sortValue: (r) => r.deepest_level,
    },
    {
      id: 'shares',
      header: t('stats.cols.shares'),
      numeric: true,
      cell: (r) => formatShares(r.shares, locale),
      sortValue: (r) => toNumber(r.shares),
    },
    {
      id: 'invested',
      header: t('stats.cols.invested'),
      numeric: true,
      cell: (r) => formatMoney(r.invested, locale),
      sortValue: (r) => toNumber(r.invested),
    },
    {
      id: 'average',
      header: t('stats.cols.average'),
      numeric: true,
      cell: (r) => formatMoney(r.average, locale),
      sortValue: (r) => toNumber(r.average),
    },
    {
      id: 'close',
      header: t('stats.cols.closePrice'),
      numeric: true,
      cell: (r) => formatMoney(r.close_price, locale),
      sortValue: (r) => toNumber(r.close_price),
    },
    {
      id: 'proceeds',
      header: t('stats.cols.proceeds'),
      numeric: true,
      cell: (r) => formatMoney(r.proceeds, locale),
      sortValue: (r) => toNumber(r.proceeds),
    },
    {
      id: 'pl',
      header: t('stats.cols.pl'),
      numeric: true,
      cell: (r) => (
        <PlValue value={toNumber(r.pl)}>{formatMoney(r.pl, locale, { signed: true })}</PlValue>
      ),
      sortValue: (r) => toNumber(r.pl),
    },
    {
      id: 'plPct',
      header: t('stats.cols.plPct'),
      numeric: true,
      cell: (r) => formatPercent(r.pl_pct, locale, { signed: true }),
      sortValue: (r) => toNumber(r.pl_pct),
    },
    {
      id: 'days',
      header: t('stats.cols.days'),
      numeric: true,
      cell: (r) => formatNumber(r.days, locale),
      sortValue: (r) => r.days,
    },
    {
      id: 'annualised',
      header: t('stats.cols.annualised'),
      numeric: true,
      cell: (r) =>
        r.annualised === null ? (
          <Tooltip content={t('stats.annualisedHidden')}>
            <span className="text-text-3">—</span>
          </Tooltip>
        ) : (
          formatPercent(r.annualised, locale, { signed: true })
        ),
      sortValue: (r) => r.annualised ?? -Infinity,
    },
    {
      id: 'regime',
      header: t('stats.cols.regime'),
      cell: (r) =>
        r.regime_trend
          ? `${t(r.regime_trend === 'up' ? 'regime.uptrend' : 'regime.downtrend')}${r.regime_vol ? ` · ${t(`regime.vol.${r.regime_vol}`)}` : ''}`
          : '—',
    },
  ];
  return (
    <section className="rounded-lg border border-border-subtle bg-bg-1">
      <h2 className="flex h-9 items-center px-3 text-sm font-medium">
        {t('stats.closedPositions')}
      </h2>
      <div className="max-h-[70vh] overflow-auto px-1 pb-1">
        <DataTable
          caption={t('stats.closedPositions')}
          columns={columns}
          rows={sortRows(rows, columns, sort)}
          rowKey={(r) => r.position_id}
          sort={sort}
          onSortChange={setSort}
          onRowClick={(r) => {
            select(r.position_id);
            void routes.position.open({ id: String(r.position_id) });
          }}
        />
      </div>
    </section>
  );
}
